/**
 * LanguageSelector — màn hình chọn 1 trong 4 ngôn ngữ.
 *
 * Hiển thị:
 *   - Lần đầu vào app (chưa có `cafechuquan_locale_chosen` trong localStorage).
 *   - Click switcher trong HudBar settings sau này.
 *
 * Sau khi chọn → setLocale + persist + navigate về trang trước hoặc /login.
 */
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  LOCALE_FLAG,
  LOCALE_NATIVE_NAME,
  SUPPORTED_LOCALES,
  setLocale,
  type SupportedLocale,
} from "@/i18n";

const LOCALE_CHOSEN_KEY = "cafechuquan_locale_chosen";

export function hasChosenLocale(): boolean {
  try {
    return localStorage.getItem(LOCALE_CHOSEN_KEY) === "true";
  } catch {
    return false;
  }
}

function markLocaleChosen(): void {
  try {
    localStorage.setItem(LOCALE_CHOSEN_KEY, "true");
  } catch {
    // SSR / private mode
  }
}

export function LanguageSelector() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  function choose(locale: SupportedLocale) {
    setLocale(locale);
    markLocaleChosen();
    // Sau khi chọn → vào flow login (hoặc về Quán nếu đã có token).
    navigate("/login", { replace: true });
  }

  const current = i18n.language as SupportedLocale;

  return (
    <main
      className="min-h-full flex items-center justify-center p-6 bg-cover bg-center relative"
      style={{ backgroundImage: "url(/assets/scenes/scene_1.jpg)" }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cream/80 backdrop-blur-sm"
      />
      <div className="relative bg-white rounded-2xl p-8 shadow-soft-amber border border-amber50/30 w-full max-w-sm">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl">☕</span>
          <h1 className="text-xl font-bold text-coffee">{t("app.title")}</h1>
        </div>
        <h2 className="text-sm font-medium text-coffee/80 mb-1">
          {t("language.select_title")}
        </h2>
        <p className="text-xs text-coffee/60 mb-5">
          {t("language.select_subtitle")}
        </p>

        <div className="space-y-2">
          {SUPPORTED_LOCALES.map((locale) => {
            const isActive = current === locale;
            return (
              <button
                key={locale}
                type="button"
                onClick={() => choose(locale)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition active:scale-[0.98] ${
                  isActive
                    ? "border-coffee bg-coffee/5"
                    : "border-coffee/15 hover:border-coffee/40 bg-white"
                }`}
              >
                <span className="text-2xl">{LOCALE_FLAG[locale]}</span>
                <span className="font-medium text-coffee-900">
                  {LOCALE_NATIVE_NAME[locale]}
                </span>
                {isActive && (
                  <span className="ml-auto text-coffee text-sm">✓</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </main>
  );
}
